import React from 'react';
import { useTranslation } from 'react-i18next';

import { EntityPickerPopover } from '@work4all/components/lib/components/entity-picker/components';
import { RythmPicker } from '@work4all/components/lib/components/entity-picker/rythm-picker/RythmPicker';

import { ServiceContractRythm } from '@work4all/models/lib/Enums/ServiceContractRythm.enum';

import { PickerTargetButton } from '../../containers/mask-overlays/locked-inputs';

interface Props {
  value: ServiceContractRythm | null;
  onChange: (value: ServiceContractRythm | null) => void;
  error?: string;
  disabled?: boolean;
  clearable?: boolean;
  name?: string;
  required?: boolean;
  onBlur?: () => void;
}

type RythmPickerItem = {
  id: ServiceContractRythm;
  name: string;
};

export const RythmPickerField = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { t } = useTranslation();
    const {
      error,
      disabled,
      value,
      onChange,
      onBlur,
      name,
      required,
      clearable,
      ...otherProps
    } = props;

    const normalizeValue = (
      val: ServiceContractRythm | RythmPickerItem | null
    ): RythmPickerItem | null => {
      if (!val) return null;

      if (typeof val === 'object' && val.id !== undefined) {
        return val as RythmPickerItem;
      }

      return {
        id: val as ServiceContractRythm,
        name: t(`SERVICECONTRACT.RYTHMSTATUSES.${val}`),
      };
    };

    const handleChange = (newValue: RythmPickerItem | null) => {
      if (!newValue) {
        onChange(null);
        return;
      }

      onChange(newValue.id);
    };

    const normalizedValue = normalizeValue(value);

    const displayValue = normalizedValue ? normalizedValue.name || '' : '';

    return (
      <EntityPickerPopover
        picker={
          <RythmPicker
            multiple={false}
            value={normalizedValue}
            onChange={handleChange}
            layout="advanced"
            {...otherProps}
          />
        }
      >
        <PickerTargetButton
          ref={ref}
          disabled={disabled}
          onClear={clearable ? () => handleChange(null) : null}
          label={t('INPUTS.RHYTHM')}
          error={error}
          value={displayValue}
          name={name}
          onBlur={onBlur}
          required={required}
        />
      </EntityPickerPopover>
    );
  }
);
